import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface OptionQuote {
  ask_price: number;
  bid_price: number;
  last_price: number;
  volume: number;
}

interface OptionContract {
  symbol: string;
  strike_price: number;
  put_call: 'put' | 'call';
  expiration_date: string;
  quote?: OptionQuote;
}

interface OptionChainByStrike {
  [strike: string]: {
    puts: OptionContract[];
    calls: OptionContract[];
  };
}

interface GroupedOptionChain {
  [date: string]: OptionChainByStrike;
}

interface OptionChainResponse {
  symbol: string;
  expirationDates: string[];
  optionChain: GroupedOptionChain;
}

interface OptionChainProps {
  symbol: string;
  selectedExpiry?: string;
  onExpiryChange?: (expiry: string) => void;
}

const OptionChain: React.FC<OptionChainProps> = ({
  symbol,
  selectedExpiry,
  onExpiryChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optionData, setOptionData] = useState<OptionChainResponse | null>(null);
  const [activeExpiry, setActiveExpiry] = useState<string | null>(null);
  const [strikeRange, setStrikeRange] = useState<[number, number]>([0, 1000]);
  const [displayedStrikes, setDisplayedStrikes] = useState<number[]>([]);
  const [underlyingPrice, setUnderlyingPrice] = useState<number | null>(null);
  const [strikeFilter, setStrikeFilter] = useState<string>('all'); // 'all', 'around', 'itm', 'otm'

  // Format date for display (YYYY-MM-DD to MMM DD, YYYY)
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format price with 2 decimal places
  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return '-';
    return price.toFixed(2);
  };

  // Fetch underlying price from Alpaca
  const fetchUnderlyingPrice = async (ticker: string) => {
    try {
      console.log(`[OptionChain] Fetching price for ${ticker}...`);
      
      // First try Alpaca API through our endpoint
      const response = await fetch(`/api/alpaca/quotes?symbols=${ticker}`);
      if (!response.ok) {
        console.warn(`Failed to fetch underlying price for ${ticker} from Alpaca API`);
        // Don't return null yet, we'll try alternative sources
      } else {
        const data = await response.json();
        console.log(`[OptionChain] Quote data received from Alpaca:`, data);
        
        // Direct access to quote - Alpaca API returns the quotes object directly
        if (data && data[ticker]) {
          const quote = data[ticker];
          console.log(`[OptionChain] Quote from Alpaca for ${ticker}:`, quote);
          
          // Use midpoint if both bid and ask available
          if (quote.bp && quote.ap) {
            const midpoint = (quote.bp + quote.ap) / 2;
            console.log(`[OptionChain] Using midpoint price: $${midpoint}`);
            return midpoint;
          } 
          // Otherwise use any available price
          else if (quote.bp) {
            console.log(`[OptionChain] Using bid price: $${quote.bp}`);
            return quote.bp;
          } 
          else if (quote.ap) {
            console.log(`[OptionChain] Using ask price: $${quote.ap}`);
            return quote.ap;
          }
          // If we have the last price, use that
          else if (quote.lp) {
            console.log(`[OptionChain] Using last price: $${quote.lp}`);
            return quote.lp;
          }
        }
        
        // Format 2: Legacy fallback format with nested structure
        else if (data && data.quotes && data.quotes[ticker]) {
          const quote = data.quotes[ticker];
          console.log(`[OptionChain] Quote from quotes object for ${ticker}:`, quote);
          
          if (quote.bp && quote.ap) {
            const midpoint = (quote.bp + quote.ap) / 2;
            console.log(`[OptionChain] Using midpoint price: $${midpoint}`);
            return midpoint;
          } else if (quote.bp) {
            console.log(`[OptionChain] Using bid price: $${quote.bp}`);
            return quote.bp;
          } else if (quote.ap) {
            console.log(`[OptionChain] Using ask price: $${quote.ap}`);
            return quote.ap;
          } else if (quote.lp) {
            console.log(`[OptionChain] Using last price: $${quote.lp}`);
            return quote.lp;
          }
        }
        
        // Format 3: Original expected format with nested quote
        else if (data && data[ticker] && data[ticker].quote) {
          const quote = data[ticker].quote;
          console.log(`[OptionChain] Quote from nested format for ${ticker}:`, quote);
          
          if (quote.bp && quote.ap) {
            const midpoint = (quote.bp + quote.ap) / 2;
            console.log(`[OptionChain] Using midpoint price: $${midpoint}`);
            return midpoint;
          } else if (quote.bp) {
            console.log(`[OptionChain] Using bid price: $${quote.bp}`);
            return quote.bp;
          } else if (quote.ap) {
            console.log(`[OptionChain] Using ask price: $${quote.ap}`);
            return quote.ap;
          } else if (quote.lp) {
            console.log(`[OptionChain] Using last price: $${quote.lp}`);
            return quote.lp;
          }
        }
        
        console.log(`[OptionChain] Could not find price in Alpaca response:`, data);
      }
      
      // If we get here, either the API call failed or we didn't find a price in the response
      // Try fetching from Yahoo Finance directly as a fallback
      console.log(`[OptionChain] Attempting to fetch from Yahoo Finance for ${ticker}...`);
      try {
        const yahooResponse = await fetch(`https://query1.finance.yahoo.com/v8/finance/quote?symbols=${ticker}`);
        if (yahooResponse.ok) {
          const yahooData = await yahooResponse.json();
          if (yahooData?.quoteResponse?.result?.[0]) {
            const quote = yahooData.quoteResponse.result[0];
            console.log(`[OptionChain] Yahoo Finance data:`, quote);
            
            // Use the regular market price
            if (quote.regularMarketPrice) {
              console.log(`[OptionChain] Using Yahoo price: $${quote.regularMarketPrice}`);
              return quote.regularMarketPrice;
            }
          }
        }
      } catch (yahooErr) {
        console.error(`[OptionChain] Error fetching from Yahoo:`, yahooErr);
      }
      
      // As a last resort, try another alternative API (Financial Modeling Prep)
      try {
        const fmpResponse = await fetch(`https://financialmodelingprep.com/api/v3/quote-short/${ticker}?apikey=demo`);
        if (fmpResponse.ok) {
          const fmpData = await fmpResponse.json();
          if (fmpData && fmpData.length > 0 && fmpData[0].price) {
            console.log(`[OptionChain] Using FMP price: $${fmpData[0].price}`);
            return fmpData[0].price;
          }
        }
      } catch (fmpErr) {
        console.error(`[OptionChain] Error fetching from FMP:`, fmpErr);
      }
      
      console.warn(`[OptionChain] Could not get price for ${ticker} from any source`);
      return null;
    } catch (err) {
      console.error('Error fetching underlying price:', err);
      return null;
    }
  };

  // Filter strikes based on underlying price and filter setting (legacy function, no longer used)
  const filterStrikes = (strikes: number[], price: number | null, filter: string) => {
    if (filter === 'all') return strikes;
    if (!price) {
      console.warn(`[OptionChain] Cannot apply filter '${filter}': No underlying price available`);
      return strikes;
    }
    
    const strikesSorted = [...strikes].sort((a, b) => a - b);
    
    if (filter === 'around') {
      // Show strikes within ~20% of current price
      const lowerBound = price * 0.8;
      const upperBound = price * 1.2;
      return strikesSorted.filter(strike => strike >= lowerBound && strike <= upperBound);
    }
    
    return strikesSorted;
  };

  useEffect(() => {
    if (!symbol) return;
    
    const fetchOptionChain = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch underlying price first
        const price = await fetchUnderlyingPrice(symbol);
        
        if (price) {
          console.log(`[OptionChain] Successfully fetched underlying price for ${symbol}: $${price}`);
          setUnderlyingPrice(price);
        } else {
          console.warn(`[OptionChain] Could not get underlying price for ${symbol}, tabs will be disabled`);
        }
        
        // If we're changing filters and already have data, no need to fetch again
        if (optionData && activeExpiry) {
          if (strikeFilter !== 'all') {
            console.log(`[OptionChain] Filter changed to ${strikeFilter}, using existing data`);
            setLoading(false);
            return;
          }
          
          if (selectedExpiry && optionData.expirationDates.includes(selectedExpiry) && 
              activeExpiry === selectedExpiry) {
            console.log(`[OptionChain] Using existing data for ${selectedExpiry}`);
            setLoading(false);
            return;
          }
        }
        
        // Explicitly specify backend URL (adjust port if necessary)
        // Use environment variable for backend URL, fallback to localhost if not set (Vite uses import.meta.env)
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        let apiUrl = `/api/alpaca/options?symbol=${encodeURIComponent(symbol)}`;
        if (selectedExpiry) {
          apiUrl += `&expiration=${encodeURIComponent(selectedExpiry)}`;
        }
        
        console.log(`[OptionChain] Fetching data from: ${apiUrl}`);
        const response = await fetch(apiUrl);
        console.log(`[OptionChain] API response status:`, response.status);
        
        if (!response.ok) {
          const errorData = await response.text();
          console.error(`[OptionChain] API error:`, errorData);
          throw new Error(`Failed to fetch option chain: ${response.status} ${errorData}`);
        }
        
        const data = await response.json();
        console.log(`[OptionChain] Data received with ${data.expirationDates?.length || 0} expiration dates`);
        
        // Extra validation and error handling
        if (!data) {
          throw new Error('Empty response from API');
        }
        
        // Check if we have any data at all
        if (!data.expirationDates || data.expirationDates.length === 0 || !data.optionChain) {
          if (data.raw?.contracts?.length > 0) {
            console.warn(`[OptionChain] API returned ${data.raw.contracts.length} contracts but no organized expiration data`);
            setError(`No organized option data for ${symbol}. Raw data is available but cannot be displayed.`);
          } else {
            console.warn(`[OptionChain] No option data available for ${symbol}`);
            setError(`No option data available for ${symbol}`);
          }
          setOptionData(null);
        } else {
          setOptionData(data);
          
          // Set active expiry to selected or first available
          if (!activeExpiry || !data.expirationDates.includes(activeExpiry)) {
            const newActiveExpiry = selectedExpiry && data.expirationDates.includes(selectedExpiry)
              ? selectedExpiry
              : data.expirationDates[0];
            
            setActiveExpiry(newActiveExpiry);
            console.log(`[OptionChain] Setting active expiry to: ${newActiveExpiry}`);
            
            if (onExpiryChange) {
              onExpiryChange(newActiveExpiry);
            }
          }
        }
      } catch (err: any) {
        console.error('Error fetching option chain:', err);
        setError(err.message || 'Failed to fetch option chain');
        setOptionData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOptionChain();
  }, [symbol, selectedExpiry, strikeFilter]);

  // Handle expiry tab change
  const handleExpiryChange = (newExpiry: string) => {
    console.log(`[OptionChain] Tab change requested to ${newExpiry}`);
    if (!newExpiry || newExpiry === activeExpiry) return;
    
    setActiveExpiry(newExpiry);
    
    if (onExpiryChange) {
      onExpiryChange(newExpiry);
    }
  };

  // Handle strike filter change
  const handleStrikeFilterChange = (filter: string) => {
    console.log(`[OptionChain] Filter change requested to ${filter}`);
    if (!filter || filter === strikeFilter) return;
    
    setStrikeFilter(filter);
    console.log(`[OptionChain] Filter set to '${filter}'`);
    
    // The filteredStrikes will be updated automatically in the render logic
  };

  if (loading) {
    return (
      <Card className="relative overflow-hidden shadow-md border-0">
        <CardContent className="flex justify-center items-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span className="ml-2 text-blue-600 font-medium">Loading option chain...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-md border-0">
        <CardHeader className="px-4 py-3 border-b border-slate-100">
          <CardTitle className="text-lg text-red-600">Option Chain Error</CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-3">
          <p className="text-red-500 text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!optionData || !activeExpiry) {
    return (
      <Card className="relative overflow-hidden shadow-md border-0">
        <CardHeader className="px-4 py-3 border-b border-slate-100">
          <CardTitle className="text-lg">Option Chain</CardTitle>
          <CardDescription>No option data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Check if the current display has both contracts (puts and calls)
  const activeExpiryData = optionData.optionChain[activeExpiry];
  
  // Determine properly which strikes have valid options for the filter
  const getValidStrikes = (filter: string): number[] => {
    if (!activeExpiryData) {
      console.warn('[OptionChain] No data for active expiry:', activeExpiry);
      return [];
    }
    
    // Log the available strikes for debugging
    const allStrikes = Object.keys(activeExpiryData).map(Number).sort((a, b) => a - b);
    console.log(`[OptionChain] Found ${allStrikes.length} strikes for ${activeExpiry}:`, allStrikes);
    
    if (allStrikes.length === 0) return [];
    
    if (filter === 'all') return allStrikes;
    
    if (!underlyingPrice) {
      console.warn("[OptionChain] Cannot apply filter without underlying price, returning all strikes");
      return allStrikes;
    }
    
    switch (filter) {
      case 'around':
        // Show strikes within ~20% of current price
        const lowerBound = underlyingPrice * 0.8;
        const upperBound = underlyingPrice * 1.2;
        return allStrikes.filter(strike => 
          strike >= lowerBound && 
          strike <= upperBound && 
          (activeExpiryData[strike]?.calls?.length > 0 || activeExpiryData[strike]?.puts?.length > 0)
        );
        
      default:
        return allStrikes;
    }
  };
  
  // Get filtered strikes based on current filter
  const filteredStrikes = getValidStrikes(strikeFilter);
  console.log(`[OptionChain] Filtered ${filteredStrikes.length} strikes for ${strikeFilter} filter`);
  
  return (
    <Card className="relative overflow-hidden shadow-lg border-0 bg-gradient-to-b from-slate-50 to-white">
      <CardHeader className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            {symbol.toUpperCase()} Options
          </CardTitle>
          {underlyingPrice ? (
            <div className="flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 px-2 py-1 rounded-md shadow-sm border border-blue-100 text-blue-700">
              <span className="font-semibold">${underlyingPrice.toFixed(2)}</span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5 rounded-full hover:bg-blue-100 transition-colors duration-200"
                onClick={async () => {
                  setLoading(true);
                  const price = await fetchUnderlyingPrice(symbol);
                  if (price) {
                    setUnderlyingPrice(price);
                    console.log(`[OptionChain] Refreshed price: $${price}`);
                  }
                  setLoading(false);
                }}
                title="Refresh market price"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 21h5v-5"/></svg>
              </Button>
            </div>
          ) : (
            <div className="text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-200 text-xs">
              No price available
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1">
          {optionData && optionData.expirationDates.length > 1 ? (
            <span className="text-xs text-gray-500">Select expiration:</span>
          ) : (
            <span className="text-xs text-gray-500">
              Exp: {activeExpiry && formatDate(activeExpiry)}
            </span>
          )}
          
          {/* Enhanced Legend */}
          {underlyingPrice && (
            <div className="ml-auto text-xs flex items-center bg-slate-50 px-2 py-1 rounded-md border border-slate-200 shadow-sm">
              <div className="flex flex-col items-center mr-2">
                <span className="text-slate-600 font-medium">Legend:</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 w-3 bg-blue-50 border border-blue-100 rounded-sm mr-1"></div>
                <span className="text-blue-700 mr-2">Call ITM</span>
                <div className="h-3 w-3 bg-red-50 border border-red-100 rounded-sm mr-1"></div>
                <span className="text-red-700 mr-2">Put ITM</span>
                <div className="h-3 w-3 bg-yellow-50 border border-yellow-200 rounded-sm mr-1"></div>
                <span className="text-yellow-800">Current</span>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="px-3 pt-2 pb-3">
        {/* Expiration Date Tabs - Sleek scrollable design */}
        {optionData.expirationDates.length > 1 && (
          <div className="mb-2 border border-slate-100 rounded-md bg-slate-50 p-0.5">
            <Tabs 
              value={activeExpiry} 
              onValueChange={handleExpiryChange}
              className="w-full"
            >
              <TabsList className="w-full flex overflow-x-auto scrollbar-hide bg-transparent h-8 p-0.5">
                {optionData.expirationDates.map((date) => (
                  <TabsTrigger 
                    key={date} 
                    value={date}
                    className="flex-1 min-w-fit cursor-pointer hover:bg-white transition-all duration-200 text-xs py-1 h-7"
                  >
                    {formatDate(date)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Strike Filter Tabs - More modern design */}
        <div className="mb-2">
          <Tabs 
            value={strikeFilter} 
            onValueChange={handleStrikeFilterChange}
            className="w-full"
          >
            <TabsList className="w-full pointer-events-auto h-8 p-0.5 bg-gradient-to-r from-blue-50 via-slate-50 to-red-50">
              <TabsTrigger 
                value="all"
                className="flex-1 cursor-pointer text-xs h-7 data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
              >
                All Strikes
              </TabsTrigger>
              <TabsTrigger 
                value="around"
                className="flex-1 cursor-pointer text-xs h-7 data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
                disabled={!underlyingPrice}
              >
                Near Price ±20%
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Option Chain Table - Modern, compact design */}
        <div className="overflow-x-auto">
          <Table className="border border-slate-100 rounded-md overflow-hidden">
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-blue-50 via-slate-100 to-red-50 border-b border-slate-200">
                <TableHead colSpan={4} className="text-center py-1.5 text-sm text-blue-700 font-medium">
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="h-2 w-2 bg-blue-50 border border-blue-200 rounded-sm"></div>
                    <span>Calls</span>
                    {underlyingPrice && <span className="text-xs">(ITM when Strike &lt; ${underlyingPrice.toFixed(2)})</span>}
                  </div>
                </TableHead>
                <TableHead className="text-center py-1.5 bg-white border-x border-slate-200 text-sm text-slate-700 font-semibold">Strike</TableHead>
                <TableHead colSpan={4} className="text-center py-1.5 text-sm text-red-700 font-medium">
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="h-2 w-2 bg-red-50 border border-red-200 rounded-sm"></div>
                    <span>Puts</span>
                    {underlyingPrice && <span className="text-xs">(ITM when Strike &gt; ${underlyingPrice.toFixed(2)})</span>}
                  </div>
                </TableHead>
              </TableRow>
              <TableRow className="bg-gradient-to-r from-blue-50 via-slate-50 to-red-50 text-xs">
                <TableHead className="text-right py-1 font-medium text-blue-900">Bid</TableHead>
                <TableHead className="text-right py-1 font-medium text-blue-900">Ask</TableHead>
                <TableHead className="text-right py-1 font-medium text-blue-900">Last</TableHead>
                <TableHead className="text-right py-1 font-medium text-blue-900">Vol</TableHead>
                <TableHead className="text-center py-1 bg-white border-x border-slate-200 font-bold text-slate-900">Price</TableHead>
                <TableHead className="text-right py-1 font-medium text-red-900">Bid</TableHead>
                <TableHead className="text-right py-1 font-medium text-red-900">Ask</TableHead>
                <TableHead className="text-right py-1 font-medium text-red-900">Last</TableHead>
                <TableHead className="text-right py-1 font-medium text-red-900">Vol</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStrikes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-4 bg-slate-50 text-slate-500">
                    No option contracts available with the selected filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredStrikes.map((strike) => {
                  const calls = activeExpiryData[strike]?.calls || [];
                  const puts = activeExpiryData[strike]?.puts || [];
                  
                  const callContract: OptionContract | undefined = calls[0];
                  const putContract: OptionContract | undefined = puts[0];
                  
                  // Determine if this strike is ITM or OTM based on underlying price
                  const isCallITM = underlyingPrice !== null && strike < underlyingPrice;
                  const isPutITM = underlyingPrice !== null && strike > underlyingPrice;
                  
                  return (
                    <TableRow key={strike} className="hover:bg-slate-50 transition-colors duration-150">
                      {/* Call data */}
                      <TableCell className={`text-right py-1.5 text-xs ${isCallITM ? 'bg-blue-50 text-blue-800' : ''}`}>
                        {formatPrice(callContract?.quote?.bid_price)}
                      </TableCell>
                      <TableCell className={`text-right py-1.5 text-xs ${isCallITM ? 'bg-blue-50 text-blue-800' : ''}`}>
                        {formatPrice(callContract?.quote?.ask_price)}
                      </TableCell>
                      <TableCell className={`text-right py-1.5 text-xs ${isCallITM ? 'bg-blue-50 text-blue-800' : ''}`}>
                        {formatPrice(callContract?.quote?.last_price)}
                      </TableCell>
                      <TableCell className={`text-right py-1.5 text-xs ${isCallITM ? 'bg-blue-50 text-blue-800' : ''}`}>
                        {callContract?.quote?.volume || '-'}
                      </TableCell>
                      
                      {/* Strike price - cleaner styling without redundant labels */}
                      <TableCell 
                        className={`text-center py-1.5 ${
                          strike === underlyingPrice 
                            ? 'bg-gradient-to-b from-yellow-50 to-yellow-100 border-y border-yellow-200' 
                            : 'bg-gradient-to-b from-white to-slate-50 border-x border-slate-200'
                        }`}
                      >
                        <div className="relative px-1">
                          {underlyingPrice && strike === underlyingPrice && (
                            <div className="absolute -right-1 -top-1">
                              <div className="h-2 w-2 bg-yellow-300 rounded-full"></div>
                            </div>
                          )}
                          <span className="text-base font-medium">{strike}</span>
                        </div>
                      </TableCell>
                      
                      {/* Put data */}
                      <TableCell className={`text-right py-1.5 text-xs ${isPutITM ? 'bg-red-50 text-red-800' : ''}`}>
                        {formatPrice(putContract?.quote?.bid_price)}
                      </TableCell>
                      <TableCell className={`text-right py-1.5 text-xs ${isPutITM ? 'bg-red-50 text-red-800' : ''}`}>
                        {formatPrice(putContract?.quote?.ask_price)}
                      </TableCell>
                      <TableCell className={`text-right py-1.5 text-xs ${isPutITM ? 'bg-red-50 text-red-800' : ''}`}>
                        {formatPrice(putContract?.quote?.last_price)}
                      </TableCell>
                      <TableCell className={`text-right py-1.5 text-xs ${isPutITM ? 'bg-red-50 text-red-800' : ''}`}>
                        {putContract?.quote?.volume || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default OptionChain;
